import type { ObjectId } from 'mongodb';

import { OrganizationStatus } from './Organizations';

export enum UserTypes {
  /** A web portal user */
  organizational = 'organizational',
  account = 'account',
  /** An API user */
  machine = 'machine',
  unknown = 'unknown',
}

// Accepts the "(555) 555-1234" shape the portal's phone input mask emits, as well as dashed, spaced,
// bare-digit and +country forms. Validity is decided by digit count (10 to 15, the E.164 maximum)
// rather than character count, because the mask emits partial values like "(555) 555-1" that pass
// on length alone. Requiring a digit at both ends (after an optional "+" and "(") rejects padding and
// stray separators. Kept as a string so it works as a JSON Schema `pattern` (ajv) and in a RegExp (yup).
export const USER_PHONE_NUMBER_PATTERN = '^(?=(?:\\D*\\d){10,15}\\D*$)\\+?\\(?\\d[\\d \\-()]{8,30}\\d$';

export enum UserPermissions {
  admin = 'admin',
  canApproveActivation = 'canApproveActivation',
  canApproveInstall = 'canApproveInstall',
  canViewVendorInvoices = 'canViewVendorInvoices',
  canImportAvlData = 'canImportAvlData',
  editor = 'editor',
  lightReachPaymentAdmin = 'lightReachPaymentAdmin',
  lightReachCapitalMarketsAdmin = 'lightReachCapitalMarketsAdmin',
  lightReachIncentiveAdmin = 'lightReachIncentiveAdmin',
  lightReachSupport = 'lightReachSupport',
  lightReachOrgPaymentAdmin = 'lightReachOrgPaymentAdmin',
  lightReachOrgPaymentViewer = 'lightReachOrgPaymentViewer',
  lightReachAccountChargesAdmin = 'lightReachAccountChargesAdmin',
  lightReachAccountingAdmin = 'lightReachAccountingAdmin',
  lightReachAccountingReader = 'lightReachAccountingReader',
  lightReachAccountingSettings = 'lightReachAccountingSettings',
  lightReachAccountingApprover = 'lightReachAccountingApprover',
  lightReachAccountingSupport = 'lightReachAccountingSupport',
  lightReachRequirementAdmin = 'lightReachRequirementAdmin',
  lightReachCreditAdmin = 'lightReachCreditAdmin',
  packageAnalyzer = 'packageAnalyzer',
  pricingAdmin = 'pricingAdmin',
  orgAccountingAdmin = 'orgAccountingAdmin',
  reader = 'reader',
  reCheckAdmin = 'reCheckAdmin',
  contractAdmin = 'contractAdmin',
  lightReachTaskAdmin = 'lightReachTaskAdmin',
  lightReachPermissionsAdmin = 'lightReachPermissionsAdmin',
  lightReachQCSettingsAdmin = 'lightReachQCSettingsAdmin',
  lightReachQCSuperAdmin = 'lightReachQCSuperAdmin',
  lightReachOrganizationSignatory = 'lightReachOrganizationSignatory',
  lightReachQCTech = 'lightReachQCTech',
  measurableGenerationAdmin = 'measurableGenerationAdmin',
  measurableGenerationViewer = 'measurableGenerationViewer',
  comfortPlanDealer = 'comfortPlanDealer',
  billingBlockerAdmin = 'billingBlockerAdmin',
  lightReachFicoAdmin = 'lightReachFicoAdmin',
  lightReachSafeHarborEquipmentPricing = 'lightReachSafeHarborEquipmentPricing',
  leadManagementAdmin = 'leadManagementAdmin',
  underwritingAdmin = 'underwritingAdmin',
  lightReachDirectPayOverride = 'lightReachDirectPayOverride',
}

export interface User {
  permissions?: UserPermissions[];
  type?: UserTypes;
  organizationId?: string;
  organizationStatus?: OrganizationStatus;
  rootOrganizationId?: string;
  rootOrganizationStatus?: OrganizationStatus;
  accountIds?: ObjectId[];
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  nickname?: string;
  userName?: string;
}

export interface userExtended extends User {
  username: string;
}

export interface Actor {
  authIdentity: string;
  user: userExtended;
}

export interface UserInfo {
  id: string;
  name: string;
}

export interface UserWithDate {
  user: UserInfo;
  date: Date;
}
